import React, { useState, useEffect } from 'react';
import { 
  EyeIcon, 
  CheckIcon, 
  XMarkIcon, 
  DocumentTextIcon,
  AcademicCapIcon,
  UserIcon,
  CalendarIcon,
  PhoneIcon,
  EnvelopeIcon,
  InformationCircleIcon,
  ClockIcon,
  ChatBubbleBottomCenterTextIcon,
  ClipboardDocumentCheckIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';

interface TeacherApplication {
  id: string;
  title: string;
  introduction: string;
  status: 'pending' | 'active' | 'rejected' | 'more_info_required';
  phoneNumber: string;
  highestDegree: string;
  graduatedUniversity: string;
  graduationYear: string;
  major: string;
  teachingExperience: string;
  resumeUrl?: string;
  portfolioUrls?: string[];
  certificateUrls?: string[];
  applicationNotes?: string;
  reviewNotes?: string;
  reviewedAt?: string;
  createdAt: string;
  user: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
}

const REVIEW_TEMPLATES = [
  "资料填写不完整，请补充详细的教学经历。",
  "上传的学历证书模糊不清，请重新上传高清图片。",
  "个人简介过于简单，无法体现您的教学特色。",
  "作品集链接无法访问，请检查。",
  "恭喜您通过审核，欢迎加入我们的教师团队！"
];

const TeacherApplications = () => {
  const [applications, setApplications] = useState<TeacherApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState<TeacherApplication | null>(null);
  const [statusFilter, setStatusFilter] = useState<'pending' | 'active' | 'rejected' | 'more_info_required'>('pending');
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, [statusFilter]);

  const fetchApplications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/teachers/applications?status=${statusFilter}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        setApplications(result.data || []);
      }
    } catch (error) {
      console.error('获取申请列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (applicationId: string, status: 'active' | 'rejected' | 'more_info_required') => {
    if (!reviewNotes.trim()) {
      alert('请填写审核意见');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/teachers/applications/${applicationId}/review`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status, reviewNotes })
      });

      if (response.ok) {
        setSelectedApplication(null);
        setReviewNotes('');
        fetchApplications();
      } else {
        alert('审核失败');
      }
    } catch (error) {
      console.error('审核失败:', error);
      alert('网络错误');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"><ClockIcon className="w-3 h-3 mr-1" />待审核</span>;
      case 'active':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"><CheckCircleIcon className="w-3 h-3 mr-1" />已通过</span>;
      case 'rejected':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"><XCircleIcon className="w-3 h-3 mr-1" />已拒绝</span>;
      case 'more_info_required':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"><ExclamationTriangleIcon className="w-3 h-3 mr-1" />需补充</span>;
      default:
        return null;
    }
  };

  const downloadFile = (filename: string) => {
    window.open(`/api/teachers/files/${filename}`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-4">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">教师入驻审核</h1>
          <p className="text-gray-500 mt-1">严格把控师资质量，打造专业教育平台</p>
        </div>
        
        {/* 状态筛选 */}
        <div className="flex bg-gray-100 p-1 rounded-lg">
          {[
            { key: 'pending', label: '待审核' },
            { key: 'active', label: '已通过' },
            { key: 'more_info_required', label: '需补充' },
            { key: 'rejected', label: '已拒绝' }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key as any)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                statusFilter === tab.key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* 申请列表 */}
      <div className="bg-white shadow-sm rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">申请人</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">资质背景</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">申请时间</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {applications.map((app) => (
                <tr key={app.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setSelectedApplication(app)}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold">
                        {app.user.lastName[0]}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{app.user.lastName}{app.user.firstName}</div>
                        <div className="text-sm text-gray-500">{app.user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{app.title}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{app.highestDegree} · {app.graduatedUniversity}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(app.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(app.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button className="text-indigo-600 hover:text-indigo-900 flex items-center justify-end w-full">
                      <EyeIcon className="h-4 w-4 mr-1" />
                      审核
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {applications.length === 0 && (
          <div className="text-center py-16">
            <ClipboardDocumentCheckIcon className="mx-auto h-12 w-12 text-gray-300" />
            <p className="mt-2 text-gray-500">暂无相关申请记录</p>
          </div>
        )}
      </div>

      {/* 审核详情抽屉/模态框 - 全屏沉浸式 */}
      {selectedApplication && (
        <div className="fixed inset-0 z-50 overflow-hidden bg-gray-900 bg-opacity-75 backdrop-blur-sm flex justify-end">
          <div className="w-full max-w-5xl bg-gray-50 h-full shadow-2xl flex flex-col animate-slideInRight">
            {/* 头部 */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center flex-shrink-0">
              <div>
                <h2 className="text-xl font-bold text-gray-900">教师入驻审核</h2>
                <p className="text-sm text-gray-500 mt-1">申请ID: {selectedApplication.id}</p>
              </div>
              <button 
                onClick={() => setSelectedApplication(null)}
                className="p-2 hover:bg-gray-100 rounded-full text-gray-500"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* 内容区 - 双栏布局 */}
            <div className="flex-1 overflow-hidden flex">
              {/* 左侧：申请资料 */}
              <div className="flex-1 overflow-y-auto p-8">
                <div className="max-w-3xl mx-auto space-y-8">
                  {/* 个人信息卡片 */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                      <UserIcon className="w-5 h-5 mr-2 text-indigo-600" />
                      基本信息
                    </h3>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="text-xs text-gray-500 uppercase font-bold">姓名</label>
                        <p className="text-gray-900 font-medium mt-1">{selectedApplication.user.lastName}{selectedApplication.user.firstName}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 uppercase font-bold">职业头衔</label>
                        <p className="text-gray-900 font-medium mt-1">{selectedApplication.title}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 uppercase font-bold">联系电话</label>
                        <p className="text-gray-900 mt-1">{selectedApplication.phoneNumber}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 uppercase font-bold">邮箱</label>
                        <p className="text-gray-900 mt-1">{selectedApplication.user.email}</p>
                      </div>
                    </div>
                  </div>

                  {/* 教育背景 */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                      <AcademicCapIcon className="w-5 h-5 mr-2 text-indigo-600" />
                      教育背景
                    </h3>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="text-xs text-gray-500 uppercase font-bold">毕业院校</label>
                        <p className="text-gray-900 font-medium mt-1">{selectedApplication.graduatedUniversity}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 uppercase font-bold">学历/学位</label>
                        <p className="text-gray-900 font-medium mt-1">{selectedApplication.highestDegree}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 uppercase font-bold">专业</label>
                        <p className="text-gray-900 mt-1">{selectedApplication.major}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 uppercase font-bold">毕业年份</label>
                        <p className="text-gray-900 mt-1">{selectedApplication.graduationYear}</p>
                      </div>
                    </div>
                  </div>

                  {/* 教学经历 */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                      <DocumentTextIcon className="w-5 h-5 mr-2 text-indigo-600" />
                      教学经历
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-xs text-gray-500 uppercase font-bold block mb-2">个人简介</label>
                        <p className="text-gray-700 bg-gray-50 p-3 rounded-lg text-sm leading-relaxed">
                          {selectedApplication.introduction}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 uppercase font-bold block mb-2">详细经历</label>
                        <p className="text-gray-700 bg-gray-50 p-3 rounded-lg text-sm leading-relaxed whitespace-pre-wrap">
                          {selectedApplication.teachingExperience}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* 附件预览 */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">附件材料</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedApplication.resumeUrl && (
                        <button 
                          onClick={() => downloadFile(selectedApplication.resumeUrl!)}
                          className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-indigo-300 transition-all"
                        >
                          <DocumentTextIcon className="w-8 h-8 text-red-500 mr-3" />
                          <div className="text-left">
                            <p className="text-sm font-medium text-gray-900">个人简历</p>
                            <p className="text-xs text-gray-500">点击预览</p>
                          </div>
                        </button>
                      )}
                      {/* 更多附件... */}
                    </div>
                  </div>
                </div>
              </div>

              {/* 右侧：审核操作面板 */}
              <div className="w-96 bg-white border-l border-gray-200 flex flex-col flex-shrink-0 shadow-lg z-10">
                <div className="p-6 border-b border-gray-200 bg-gray-50">
                  <h3 className="font-bold text-gray-900 mb-2">审核操作</h3>
                  <p className="text-xs text-gray-500">请仔细核对申请人信息的真实性</p>
                </div>
                
                <div className="p-6 flex-1 overflow-y-auto">
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      快速反馈模板
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {REVIEW_TEMPLATES.map((tpl, i) => (
                        <button
                          key={i}
                          onClick={() => setReviewNotes(tpl)}
                          className="px-3 py-1.5 bg-gray-100 hover:bg-indigo-50 hover:text-indigo-600 text-xs rounded-md transition-colors text-left border border-transparent hover:border-indigo-200"
                        >
                          {tpl}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      审核意见 *
                    </label>
                    <textarea
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      rows={6}
                      className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="请输入详细的审核反馈..."
                    />
                  </div>

                  {/* 历史审核记录 */}
                  {selectedApplication.reviewNotes && (
                    <div className="mt-8 pt-6 border-t border-gray-200">
                      <h4 className="text-xs font-bold text-gray-400 uppercase mb-3">历史记录</h4>
                      <div className="bg-gray-50 p-3 rounded-lg text-sm">
                        <p className="text-gray-600">{selectedApplication.reviewNotes}</p>
                        <p className="text-xs text-gray-400 mt-2 text-right">
                          {formatDate(selectedApplication.reviewedAt!)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="p-6 border-t border-gray-200 bg-gray-50">
                  <div className="grid grid-cols-1 gap-3">
                    <button
                      onClick={() => handleReview(selectedApplication.id, 'active')}
                      disabled={submitting}
                      className="w-full flex justify-center items-center px-4 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 font-bold shadow-lg shadow-green-200 transition-all hover:-translate-y-0.5"
                    >
                      <CheckIcon className="w-5 h-5 mr-2" />
                      {submitting ? '处理中...' : '通过审核'}
                    </button>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => handleReview(selectedApplication.id, 'more_info_required')}
                        disabled={submitting}
                        className="flex justify-center items-center px-4 py-2 bg-white border border-blue-200 text-blue-700 rounded-lg hover:bg-blue-50 font-medium"
                      >
                        <ChatBubbleBottomCenterTextIcon className="w-5 h-5 mr-2" />
                        补充资料
                      </button>
                      <button
                        onClick={() => handleReview(selectedApplication.id, 'rejected')}
                        disabled={submitting}
                        className="flex justify-center items-center px-4 py-2 bg-white border border-red-200 text-red-700 rounded-lg hover:bg-red-50 font-medium"
                      >
                        <XMarkIcon className="w-5 h-5 mr-2" />
                        拒绝申请
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherApplications;